/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Fetches calendar and conflict appointment collections for ViewAppointments.
 * @module logic/appointments/loadCalendarAppointments
 */

import { getEventAppointmentsCalendar } from "../appointment/index.js";
import { buildCalendarScopeParams } from "./appointmentCalendarParams.js";

/**
 * Appointments present in conflict scope but not in the buyer-scoped calendar list.
 * @param {Array<Object>} calendarItems
 * @param {Array<Object>} conflictItems
 * @returns {Array<Object>}
 */
export const deriveSenderAppointments = (calendarItems, conflictItems) =>
  conflictItems.filter(
    (conflictItem) =>
      !calendarItems.some(
        (calendarItem) => calendarItem._id === conflictItem._id,
      ),
  );

/**
 * Loads calendar appointments plus conflict/sender collections for the current scope.
 * @param {Object} options
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {string} options.eventId
 * @param {Object} options.userData
 * @param {Object|null|undefined} options.selectedBuyerCompany
 * @param {Object|null|undefined} options.selectedBuyerExecutive
 * @returns {Promise<{ appointments: Array<Object>, conflictItems: Array<Object>, senderAppointments: Array<Object> }>}
 */
export const loadCalendarAppointments = async ({
  navigate,
  eventId,
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
}) => {
  const { scopedParams, conflictParams } = buildCalendarScopeParams({
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
  });

  const [calendarItems, conflictItems] = await Promise.all([
    getEventAppointmentsCalendar(navigate, eventId, scopedParams),
    getEventAppointmentsCalendar(navigate, eventId, conflictParams),
  ]);

  return {
    appointments: calendarItems,
    conflictItems,
    senderAppointments: deriveSenderAppointments(calendarItems, conflictItems),
  };
};
